import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Mui } from 'components';

import { usePageContext } from '../Page/PageContext';
import PageBanner from '../PageBanner';

const useStyles = Mui.makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 2),
    minHeight: theme.spacing(5),
  },
  lead: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  badge: { display: 'flex', alignItems: 'center' },
  badgeIcon: { marginRight: theme.spacing(1) },
  breadcrumbs: {
    '& .MuiBreadcrumbs-ol': { alignItems: 'center' },
    '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' },
    '& .MuiBreadcrumbs-separator': { display: 'flex', alignItems: 'center', margin: 0 },
  },
  homeLink: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 0,
  },
  homeIcon: {
    display: 'block',
    lineHeight: 1,
    overflow: 'hidden',
  },
  crumb: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
  },
  crumbCurrent: {
    fontWeight: 600,
  },
  tags: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
  },
  ctas: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  actions: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-1),
  },
  activities: {
    display: 'flex',
    boxSizing: 'border-box',
    alignItems: 'center',
    alignContent: 'center',
    justifyItems: 'flex-end',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    color: theme.palette.grey[700],
    backgroundColor: theme.palette.grey[100],
  },
}));

const renderCrumb = (item, index, classes, isCurrent) => {
  if (item.to)
    return (
      <Mui.Link
        key={`${index.toString()}`}
        className={classes.crumb}
        variant="body1"
        color="inherit"
        to={item.to}
        component={RouterLink}
        target={item.target}>
        {item.label}
      </Mui.Link>
    );

  return (
    <Mui.Typography
      key={`${index.toString()}`}
      variant="body1"
      component={isCurrent ? 'h3' : 'p'}
      color={isCurrent ? 'textPrimary' : 'textSecondary'}
      className={clsx(classes.crumb, isCurrent && classes.crumbCurrent)}
      aria-current={isCurrent ? 'page' : undefined}>
      {item.label}
    </Mui.Typography>
  );
};

function Breadcrumb({ homeTo, tree, title }) {
  const classes = useStyles();

  const crumbs = [...(tree ?? []), ...(title ? [{ label: title }] : [])];

  if (!homeTo && !crumbs.length) return null;

  return (
    <Mui.Box displayPrint="none">
      <Mui.Breadcrumbs
        className={classes.breadcrumbs}
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        maxItems={6}>
        {homeTo && (
          <Mui.Link className={classes.homeLink} color="inherit" component={RouterLink} to={homeTo}>
            <Mui.Icon fontSize="small" className={classes.homeIcon}>
              home
            </Mui.Icon>
          </Mui.Link>
        )}
        {crumbs.map((item, index) =>
          renderCrumb(item, index, classes, index === crumbs.length - 1),
        )}
      </Mui.Breadcrumbs>
    </Mui.Box>
  );
}

Breadcrumb.propTypes = {
  homeTo: PropTypes.string,
  tree: PropTypes.array,
  title: PropTypes.any,
};

function Header(props) {
  const {
    className,
    overtitle,
    title: titleProp,
    subtitle,
    tree,
    actions,
    cta,
    secondaryCta,
    homeTo,
    extra,
    badges,
    tags,
    activities,
    ...rest
  } = props;

  const classes = useStyles();
  const { title: contextTitle } = usePageContext();

  const title = titleProp ?? contextTitle;
  const showSecondaryCta = !!secondaryCta && !secondaryCta.hidden;
  const showCta = !!cta && !cta.hidden;

  const [activitiesOpen, setActivitiesOpen] = useState(false);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <PageBanner placement="global" pb={2} />
      {!!overtitle && (
        <Mui.Typography variant="overline" color="primary">
          {overtitle}
        </Mui.Typography>
      )}
      <div className={classes.row}>
        <div className={classes.lead}>
          <Breadcrumb homeTo={homeTo} tree={tree} title={title} />
          {!!tags && <div className={classes.tags}>{tags}</div>}
        </div>
        {(showSecondaryCta || showCta) && (
          <Mui.Box className={classes.ctas} displayPrint="none">
            {showSecondaryCta && (
              <Mui.Button
                variant="outlined"
                color="secondary"
                startIcon={<Mui.Icon>{secondaryCta.icon}</Mui.Icon>}
                onClick={secondaryCta.onClick}
                disabled={secondaryCta.disabled}
                disableElevation
                {...secondaryCta.buttonProps}>
                {secondaryCta.label}
              </Mui.Button>
            )}
            {showCta && (
              <Mui.Button
                color="primary"
                variant="contained"
                startIcon={<Mui.Icon>{cta.icon}</Mui.Icon>}
                onClick={cta.onClick}
                disabled={cta.disabled}
                {...cta.buttonProps}>
                {cta.label}
              </Mui.Button>
            )}
          </Mui.Box>
        )}
      </div>
      {!!badges && <div className={classes.badges}>{badges}</div>}
      {!!subtitle && (
        <Mui.Typography className={classes.subtitle} variant="subtitle1" color="textSecondary">
          {subtitle}
        </Mui.Typography>
      )}
      {!!extra && (
        <Mui.Box mt={2}>
          <Mui.Grid container spacing={2}>
            {extra.map((item, index) => (
              <Mui.Grid item key={`${index.toString()}`}>
                <Mui.Box color="text.secondary" width="100%">
                  <div className={classes.badge}>
                    <Mui.Icon fontSize="small" className={classes.badgeIcon}>
                      {item.icon}
                    </Mui.Icon>
                    <Mui.Typography variant="body2" color="inherit" component="span">
                      {item.label}
                    </Mui.Typography>
                  </div>
                </Mui.Box>
              </Mui.Grid>
            ))}
          </Mui.Grid>
        </Mui.Box>
      )}
      <PageBanner pt={2} />
      {!!actions && (
        <div className={classes.actions}>
          {actions.map(action => {
            const button = (
              <Mui.Button
                variant="text"
                color="default"
                key={action.key}
                onClick={action.onClick}
                disabled={action.disabled}
                startIcon={<Mui.Icon>{action.icon}</Mui.Icon>}>
                {action.label}
              </Mui.Button>
            );

            if (!action.tooltip) return button;

            return (
              <Mui.Tooltip key={action.key} title={action.tooltip}>
                <span>{button}</span>
              </Mui.Tooltip>
            );
          })}
        </div>
      )}
      {!!activities && (
        <>
          <Mui.Box className={classes.activities}>
            <Mui.Button
              color="default"
              variant="text"
              size="small"
              startIcon={<Mui.Icon>history</Mui.Icon>}
              onClick={() => setActivitiesOpen(true)}
              disabled={activities.disabled}
              {...activities.buttonProps}>
              Histórico de atividades
            </Mui.Button>
          </Mui.Box>
          {!!activities.Dialog &&
            activities.Dialog?.({
              url: activities.url,
              open: activitiesOpen,
              onClose: () => setActivitiesOpen(false),
              dialogProps: activities.dialogProps,
            })}
        </>
      )}
    </div>
  );
}

Header.propTypes = {
  className: PropTypes.string,
  homeTo: PropTypes.string,
  overtitle: PropTypes.any,
  title: PropTypes.any,
  subtitle: PropTypes.any,
  tree: PropTypes.array,
  actions: PropTypes.array,
  extra: PropTypes.array,
  badges: PropTypes.node,
  tags: PropTypes.node,
  cta: PropTypes.object,
  secondaryCta: PropTypes.object,
  activities: PropTypes.object,
};

export default Header;
